import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Fab,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon,
  Logout as LogoutIcon,
  Map as MapIcon,
  List as ListIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  Directions as DirectionsIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { logout } from '../store/slices/authSlice';

const drawerWidth = 240;

function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Calendar', icon: <CalendarIcon />, path: '/dashboard/calendar' },
    { text: 'Map', icon: <MapIcon />, path: '/dashboard/map' },
    { text: 'Friends', icon: <PeopleIcon />, path: '/dashboard/friends' },
    { text: 'Profile', icon: <PersonIcon />, path: '/dashboard/profile' },
    { text: 'Subscription', icon: <CreditCardIcon />, path: '/dashboard/subscription' },
  ];

  const drawer = (
    <div>
      {/* User info in sidebar - moved from top bar */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)', mt: 2 }}>
        <Box display="flex" alignItems="center">
          <Avatar
            src={user?.profilePicture}
            alt={user?.name || 'User'}
            sx={{
              width: 32,
              height: 32,
              marginRight: 2,
            }}
          />
          <Typography variant="body1" sx={{ color: 'white' }}>
            {user?.name}
          </Typography>
        </Box>
      </Box>
      
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => {
              navigate(item.path);
              setMobileOpen(false);
            }}
          >
            <ListItemIcon sx={{ color: 'white' }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} sx={{ color: 'white' }} />
          </ListItem>
        ))}
        <ListItem button onClick={handleLogout}>
          <ListItemIcon sx={{ color: 'white' }}>
            <LogoutIcon />
          </ListItemIcon >
          <ListItemText primary="Logout" sx={{ color: 'white' }}/>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* Floating menu button for mobile - hidden on Dashboard page */}
      {location.pathname !== '/dashboard' && (
        <Fab
          color="primary"
          aria-label="menu"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1300,
            display: { xs: 'flex', sm: 'none' }, // Only show on mobile
          }}
        >
          <MenuIcon />
        </Fab>
      )}
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
            disableAutoFocus: true,
            disableEnforceFocus: true,
            disableRestoreFocus: true,
            'aria-hidden': false
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: 'linear-gradient(180deg, #FF7B6B 0%, #FF5B4B 100%)'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: 'linear-gradient(180deg, #FF7B6B 0%, #FF5B4B 100%)'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: '#FFF5E6'
        }}
      >
        <Outlet context={{ handleDrawerToggle }} />
      </Box>
    </Box>
  );
}

export default Layout; 